import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UserContext } from "../UserContext";
import { ThemeContext } from "../ThemeContext";
import {
  BarChart3,
  Check,
  ClipboardList,
  History,
  Trophy,
  User,
  Users,
} from "lucide-react";
import VideoViewer from "../components/VideoViewer";

const HomePage = () => {
  const { user } = useContext(UserContext);
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const videoRef = useRef(null);

  // Memoize title gradient
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  useEffect(() => {
    if (user) {
      navigate('/options', { replace: true });
    }
  }, [user, navigate]);

  // Set video playback speed
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 1.5;
    }
  }, []);

  // Endorsements
  const endorsements = [
    {
      quote: 'This game is great! The point-based scoring system really helped keep me motivated, and the explanations for all the answers were detailed and easy to understand, helping me improve significantly in a short span of time - especially in the writing section.',
      author: 'Aashrith',
    },
    {
      quote: 'This game is easy to use.',
      author: 'Mike',
    },
    {
      quote: 'This game gives really detailed analytics. That\'s helpful for showing what I would need to target.',
      author: 'Ben',
    },
    {
      quote: 'I think it is really fun that I can play games with my friends while studying for the SAT.',
      author: 'Yash',
    },
  ];

  const trustedResults = [
    {
      icon: Trophy,
      title: 'Students stay motivated and keep showing up.',
      body: 'Game mechanics, live competition, and clear progress tracking make it easier to keep prep consistent week after week.',
    },
    {
      icon: BarChart3,
      title: 'Users see clearer category-by-category growth.',
      body: 'Performance breakdowns help students focus on the exact reading, writing, and math skills that need attention.',
    },
    {
      icon: Users,
      title: 'Competing with friends makes practice more engaging.',
      body: 'Multiplayer modes turn SAT prep into something social, which helps learners stay accountable and improve faster.',
    },
    {
      icon: Check,
      title: 'Detailed answer review leads to smarter repetition.',
      body: 'Students can revisit missed questions, understand mistakes, and avoid repeating the same patterns on test day.',
    },
  ];

  // Slideshow state for endorsements
  const [currentEndorsementIndex, setCurrentEndorsementIndex] = useState(0);
  const totalSlides = trustedResults.length;

  // Auto-rotate trusted results
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentEndorsementIndex((prev) => (prev + 1) % totalSlides);
    }, 6000);

    return () => clearInterval(interval);
  }, [totalSlides]);

  const activeTrustedResult = trustedResults[currentEndorsementIndex];

  // Pricing plans
  const plans = [
    {
      id: 'monthly',
      name: 'Monthly Plan',
      price: '$29.99',
      period: '/Month',
      totalBilling: null,
      savings: 'Solid Value!',
      description: 'Auto-renews every 30 days.',
      features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions'],
      highlighted: false,
    },
    {
      id: 'three-month',
      name: 'Three-Month Plan',
      price: '$25.99',
      period: '/Month',
      totalBilling: '$77.97 billed every 90 days',
      savings: 'A Great Deal!',
      description: 'Auto-renews every three months.',
      features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions'],
      highlighted: false,
    },
    {
      id: 'six-month',
      name: 'Six-Month Plan',
      price: '$19.99',
      period: '/Month',
      totalBilling: '$119.94 billed every 180 days',
      savings: '33% Savings!',
      description: 'Auto-renews every six months.',
      features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions'],
      highlighted: false,
    },
    {
      id: 'annual',
      name: 'Annual Plan',
      price: '$16.50',
      period: '/Month',
      totalBilling: '$198.00 billed annually.',
      savings: 'Best Deal! 45% Savings!',
      description: 'Does not auto-renew.',
      features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions'],
      highlighted: true,
    },
  ];

  const featureShowcaseItems = [
    {
      id: 'solo',
      label: 'Solo Mode',
      videoFile: 'Solo_Mode.mp4',
      Icon: User,
      eyebrow: 'Focused Prep',
      title: 'Train on your own schedule.',
      description: 'Jump into targeted practice rounds and build confidence one concept at a time.',
      metrics: ['Adaptive pacing', 'Instant feedback', 'Skill-specific drills'],
    },
    {
      id: 'compete',
      label: 'Compete Mode',
      videoFile: 'LONGCOMPETE.mp4',
      Icon: Users,
      eyebrow: 'Live Competition',
      title: 'Turn studying into a head-to-head challenge.',
      description: 'Race friends in fast SAT battles and stay motivated with real-time pressure.',
      metrics: ['Live matches', 'Rank progression', 'Friendly rivalries'],
    },
    {
      id: 'practice',
      label: 'Practice Mode',
      videoFile: 'Practice_Mode.mp4',
      Icon: ClipboardList,
      eyebrow: 'Deliberate Practice',
      title: 'Sharpen weak areas with guided reps.',
      description: 'Work through curated question sets designed to improve speed and accuracy.',
      metrics: ['Section drills', 'Explanation review', 'Retry missed questions'],
    },
    {
      id: 'history',
      label: 'Answer History',
      videoFile: 'Answer_History.mp4',
      Icon: History,
      eyebrow: 'Review Smarter',
      title: 'See what you missed and why it matters.',
      description: 'Track previous attempts so patterns are easier to spot before test day.',
      metrics: ['Missed-question log', 'Progress snapshots', 'Better retention'],
    },
    {
      id: 'leaderboard',
      label: 'Leaderboard',
      videoFile: 'Leaderboard.mp4',
      Icon: Trophy,
      eyebrow: 'Momentum Builder',
      title: 'Climb rankings while you prep.',
      description: 'Compete for position, earn bragging rights, and stay consistent every week.',
      metrics: ['Global standings', 'Point streaks', 'Friendly competition'],
    },
    {
      id: 'analytics',
      label: 'Analytics',
      videoFile: 'Analytics.mp4',
      Icon: BarChart3,
      eyebrow: 'Performance Insights',
      title: 'Deep dive into your performance metrics.',
      description: 'Break down results by category so you know exactly what to improve next.',
      metrics: ['Reading: 84%', 'Writing: 91%', 'Math: 88%'],
    },
  ];

  const [activeFeatureId, setActiveFeatureId] = useState('analytics');
  const activeFeature = featureShowcaseItems.find((item) => item.id === activeFeatureId) || featureShowcaseItems[0];

  // If user is logged in, the component will redirect. Return null to avoid rendering anything.
  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen pb-24 overflow-hidden bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <div className="relative flex items-center pt-24 pb-10 md:pt-36 md:pb-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left Column - Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, type: "spring" }}
              className="flex flex-col justify-center items-center space-y-6 md:space-y-8 text-center"
            >
              {/* Main Headline */}
              <h1
                key={`hero-title-${isDarkMode}`}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-zonaProUltra mb-2 md:mb-4 relative inline-block max-w-[12ch]"
                style={{
                  color: isDarkMode ? '#ff7a00' : '#212D82',
                  background: titleGradient,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shine 10s linear infinite',
                  lineHeight: '1.2',
                  paddingBottom: '0.25rem'
                }}
              >
                Level Up Your Score.
              </h1>

              {/* Description Text */}
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 dark:text-gray-300 font-zonaProRegular leading-relaxed max-w-xl px-2 sm:px-0">
                Game Your SAT is the competitive test prep that is a fun, easy way to get ready for the Digital SAT.
              </p>

              {/* CTA Button - Play Now */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-fit"
              >
                <Link
                  to="/signup"
                  className="inline-block px-8 sm:px-10 py-3.5 sm:py-4 bg-[#ff7a00] hover:bg-[#e66900] text-white font-bold font-zonaProUltra text-base sm:text-lg rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Play Now
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Column - Hero Image/Animation */}
            <motion.div
              initial={{ opacity: 0, x: 50, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2, type: "spring" }}
               className="flex items-center justify-center h-full"
            >
              <div className="relative w-full max-w-[560px] h-[180px] sm:h-[220px] md:h-[260px] lg:h-[240px] flex items-center justify-center group cursor-pointer">
                {/* Video Animation */}
                <video
                  ref={videoRef}
                  src="/img/GameYourSAT4 Video.webm"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-contain object-center"
                  aria-label="GYSAT test animation"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Below the Fold Content */}
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-12 md:py-16 bg-white dark:bg-gray-900">
        {/* Feature Showcase Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="max-w-7xl mx-auto mb-16 md:mb-24"
        >
          {/* Section Heading */}
          <div className="text-center mb-8 md:mb-14 px-2">
            <h2
              key={`showcase-title-${isDarkMode}`}
              className="text-3xl sm:text-4xl md:text-6xl font-bold font-zonaProUltra mb-4 inline-block relative max-w-[14ch]"
              style={{
                color: isDarkMode ? '#ff7a00' : '#212D82',
                background: titleGradient,
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'shine 10s linear infinite',
                lineHeight: '1.2',
                paddingBottom: '0.25rem'
              }}
            >
              Everything You Need to Succeed.
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 font-zonaProRegular max-w-3xl mx-auto">
              Explore features engineered to highlight your weaknesses and maximize your score.
            </p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)] gap-5 md:gap-6 lg:gap-8 items-stretch">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 h-full gap-3 md:gap-4">
              {featureShowcaseItems.map((item) => {
                const isActive = item.id === activeFeatureId;
                const Icon = item.Icon;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveFeatureId(item.id)}
                    className={`w-full min-h-[84px] rounded-[24px] md:rounded-[28px] px-5 sm:px-6 py-4 sm:py-5 text-left transition-all duration-300 flex items-center ${isActive
                      ? 'bg-[#212D82] dark:bg-[#ff7a00] text-white shadow-xl'
                      : 'bg-gray-100 dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-800'
                      }`}
                  >
                    <div className="flex items-center gap-4 w-full">
                      <Icon className="w-6 h-6 shrink-0" />
                      <span className="text-lg sm:text-xl font-zonaProUltra leading-tight">{item.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="rounded-[32px] border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#10192b] overflow-hidden flex flex-col h-full">
              <div className="flex-1 px-4 py-6 sm:px-6 sm:py-8 md:px-10 md:py-10 bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-[#131f35] dark:via-[#111a2c] dark:to-[#0c1424] flex flex-col justify-center">
                <div className="max-w-3xl mt-auto">

                  <h3 className="text-2xl sm:text-3xl md:text-5xl font-zonaProUltra text-gray-900 dark:text-white mb-3 md:mb-4 leading-tight max-w-[14ch]">
                    {activeFeature.title}
                  </h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 font-zonaProRegular leading-relaxed">
                    {activeFeature.description}
                  </p>
                </div>
                <div className="w-full mt-6 md:mt-8 relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-white/10 bg-black/5 dark:bg-white/5 flex items-center justify-center min-h-[220px] sm:min-h-[280px] md:min-h-[360px]">
                  <VideoViewer
                    fileName={activeFeature.videoFile}
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    className="w-full h-full object-contain"
                  />
                </div>



              </div>
            </div>
          </div>
        </motion.div>

        {/* Trusted Results */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="max-w-5xl mx-auto text-center mb-16 md:mb-20 px-2"
        >
          <h2
            key={`trusted-results-${isDarkMode}`}
            className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-6 md:mb-8 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Trusted Results
          </h2>

          <div className="rounded-[32px] border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#10192b] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentEndorsementIndex}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
                className="grid grid-cols-1 md:grid-cols-[180px_minmax(0,1fr)] gap-5 md:gap-6 items-center px-4 py-6 sm:px-6 sm:py-8 md:px-10 md:py-10"
              >
                <div className="flex justify-center">
                  <div className="w-24 h-24 md:w-28 md:h-28 rounded-[28px] bg-[#212D82]/10 dark:bg-[#ff7a00]/15 text-[#212D82] dark:text-[#ff7a00] flex items-center justify-center">
                    {(() => {
                      const TrustedIcon = activeTrustedResult.icon;
                      return <TrustedIcon className="w-11 h-11 md:w-12 md:h-12" />;
                    })()}
                  </div>
                </div>

                <div className="text-center md:text-left">
                  <h3 className="text-xl sm:text-2xl md:text-4xl font-zonaProUltra text-gray-900 dark:text-white leading-tight mb-3 md:mb-4">
                    {activeTrustedResult.title}
                  </h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 font-zonaProRegular leading-relaxed mb-4 md:mb-5">
                    {activeTrustedResult.body}
                  </p>
                  <p className="text-base md:text-lg italic text-gray-700 dark:text-gray-200 font-zonaProRegular">
                    "{endorsements[currentEndorsementIndex].quote}" - {endorsements[currentEndorsementIndex].author}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="pb-8 flex justify-center items-center gap-4">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentEndorsementIndex(index)}
                  className={`rounded-full transition-all duration-300 ${currentEndorsementIndex === index
                    ? 'w-4 h-4 bg-[#ff7a00]'
                    : 'w-3 h-3 bg-gray-300 dark:bg-gray-600'
                    }`}
                  aria-label={`Go to trusted result ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </motion.div>

        {/* Pricing Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto mb-10 md:mb-14 px-2"
        >
          <h3
            key={`free-trial-h3-${isDarkMode}`}
            className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-4 md:mb-5 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Try it today for FREE for 7 days!
          </h3>
          <p className="text-base sm:text-lg md:text-2xl text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed">
            Flexible pricing lets you practice at your own pace.
            <br className="hidden md:block" />
            See how much your score can improve!
          </p>
        </motion.div>

        {/* Pricing Plans */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6 max-w-7xl mx-auto mb-12 md:mb-14">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="h-full"
            >
              <div
                className={`h-full rounded-[28px] md:rounded-[30px] px-5 py-7 md:px-7 md:py-9 text-center border transition-all duration-300 ${plan.highlighted
                  ? 'border-4 border-[#ff7a00] bg-white dark:bg-[#0f1728] shadow-[0_16px_40px_rgba(255,122,0,0.16)]'
                  : 'border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#10192b]'
                  }`}
              >
                <div className="flex h-full flex-col items-center">
                  {plan.highlighted ? (
                    <div className="ml-auto mb-4 flex h-9 w-9 items-center justify-center rounded-full bg-[#ff7a00] text-white">
                      <Check className="w-5 h-5" />
                    </div>
                  ) : (
                    <div className="mb-4 h-9" />
                  )}

                  <h4 className="text-xl sm:text-2xl font-zonaProUltra text-gray-900 dark:text-white mb-5 md:mb-6">{plan.name}</h4>

                  <div className="mb-4">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-3xl sm:text-4xl font-zonaProUltra text-gray-900 dark:text-white">{plan.price}</span>
                      <span className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 font-zonaProRegular">{plan.period}</span>
                    </div>
                    <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 font-zonaProRegular mt-3 min-h-[48px]">
                      {plan.totalBilling || 'Billed monthly'}
                    </p>
                  </div>

                  <div className={`mb-6 rounded-full px-4 sm:px-5 py-3 text-base sm:text-lg font-zonaProUltra ${plan.highlighted
                    ? 'bg-[#212D82] text-white dark:bg-[#ff7a00]'
                    : 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-white'
                    }`}>
                    {plan.savings}
                  </div>

                  <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular mt-auto">
                    {plan.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="mb-6">
            <Link
              to="/signup"
              className="inline-block min-w-[220px] sm:min-w-[260px] px-8 sm:px-10 py-4 sm:py-5 bg-[#ff7a00] hover:bg-[#e66900] text-white rounded-full font-bold font-zonaProUltra text-lg sm:text-xl transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Level Up Now
            </Link>
          </motion.div>

          <Link
            to="/terms"
            className="text-lg italic underline text-gray-800 dark:text-gray-200 hover:text-[#ff7a00] dark:hover:text-[#ff9d3d] transition-colors font-zonaProRegular"
          >
            Cancel anytime. See Terms & Conditions.
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default HomePage;
